// utils/parseProse.ts — parser for ProfProse inline markup; no HTML injection, no dangerouslySetInnerHTML

export type ProseNode =
  | { type: 'text'; text: string }
  // A hard line break inside a paragraph — emitted by parseBlocks() when a
  // single \n separates two lines of the same paragraph. The inline parser
  // (parseProse) never produces this; it only appears in block children.
  | { type: 'break' }
  | { type: 'bold'; children: ProseNode[] }
  | { type: 'italic'; children: ProseNode[] }
  | { type: 'code'; text: string }
  | { type: 'cite'; id: string }
  // principleRef: inline plain-text references like "arg11 P3" or "arg11_P3"
  // that the Prof sometimes drops in Chiedi responses. We normalize both
  // variants to a canonical principle id (argN_PM) and preserve the raw
  // spelling so the rendered link reads like the original sentence.
  | { type: 'principleRef'; argomentoId: string; principleId: string; raw: string }
  // image: the rebuilt Prof's [[image:KIND:ID]] markup (Pre-Socio Rebuild
  // Session A, Task 8). Three kinds — sign / question / scenario — resolved
  // by the InlineImage component at render time. The id payload is whatever
  // the Prof emitted between the second colon and the closing brackets:
  // for sign markup an Italian semantic name, for question markup a
  // question_id, for scenario markup a scenario name.
  | { type: 'image'; imageKind: 'sign' | 'question' | 'scenario'; id: string };

// ── BLOCK-LEVEL nodes (FP3a) ────────────────────────────────────────────────
// Produced by parseBlocks(). Each block carries INLINE ProseNode[] children
// (parsed with the existing inline parser), so bold/italic/cite/image/
// principleRef keep working inside list items, table cells, headings, and
// paragraphs. The backend prompt currently bans lists/tables; once FP3c lifts
// that ban, the Prof's markdown-ish output flows through these nodes. Until
// then, prose-only answers parse to a sequence of `paragraph` blocks that
// render byte-identically to the old \n\n-split <p> output.
export type BlockNode =
  // A normal text paragraph. A single \n inside becomes a hard line break
  // (children may contain { type: 'break' } inline nodes — see below).
  | { type: 'paragraph'; children: ProseNode[] }
  // ## / ### → a styled section label (NOT a giant h1). `level` is 2 or 3.
  | { type: 'heading'; level: 2 | 3; children: ProseNode[] }
  // Unordered (- / * / •) or ordered (1. 2.) list. Each item is inline content.
  | { type: 'list'; ordered: boolean; items: ProseNode[][] }
  // GitHub-style pipe table. `header` is the first row's cells; `rows` the body.
  // Each cell is inline content. No col-span / nesting.
  | { type: 'table'; header: ProseNode[][]; rows: ProseNode[][][] };

// Token types produced by the first-pass tokenizer
type Token =
  | { kind: 'text'; text: string }
  | { kind: 'bold_marker' }
  | { kind: 'italic_marker' }
  | { kind: 'code_marker' }
  | { kind: 'cite'; id: string }
  | { kind: 'principle_ref'; argomentoId: string; principleId: string; raw: string }
  | { kind: 'image'; imageKind: 'sign' | 'question' | 'scenario'; id: string };

const CITE_RE = /\[\[cite:([A-Za-z0-9_-]+)\]\]/g;
// Matches [[image:sign:NAME]], [[image:question:Q123]], [[image:scenario:NAME]],
// and the kind-less fallback [[image:NAME]] (treated as kind=sign — the model
// occasionally drops the `sign:` prefix and a raw marker must never reach the
// student as literal text; observed live 2026-06-11).
// The id payload is permissive (any chars except ']') because for kind=sign
// the Prof emits the Italian semantic name verbatim from `image_descriptions.json`
// — these names contain spaces, accented letters, em-dashes, slashes, digits, etc.
const IMAGE_RE = /^\[\[image:(?:(sign|question|scenario):)?([^\]]+)\]\]/;
// Matches "arg11 P3", "arg11_P3", "arg 11 P 3". Case-insensitive. Word-boundary
// leading so we don't misfire on "marginal" etc. The Prof tends to produce one
// of these three spellings depending on whether the underlying citation was an
// id or a natural-language reference.
const PRINCIPLE_REF_RE = /^arg\s*(\d{1,2})[\s_]*P\s*(\d{1,3})\b/i;
const BOLD_MARKER = '**';
const ITALIC_MARKER = '*';
const CODE_MARKER = '`';

/**
 * Tokenize a single-paragraph string into a flat token list.
 * Precedence (highest first): [[cite:ID]] → ** → * → `
 * Unclosed or malformed [[cite:...]] is returned as raw text.
 */
function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < source.length) {
    // 1. [[cite:ID]] or [[image:KIND:ID]]
    if (source[i] === '[' && source[i + 1] === '[') {
      const rest = source.slice(i);
      const cite = rest.match(/^\[\[cite:([A-Za-z0-9_-]+)\]\]/);
      if (cite) {
        tokens.push({ kind: 'cite', id: cite[1] });
        i += cite[0].length;
        continue;
      }
      const image = rest.match(IMAGE_RE);
      if (image) {
        tokens.push({
          kind: 'image',
          imageKind: (image[1] as 'sign' | 'question' | 'scenario' | undefined) ?? 'sign',
          id: image[2],
        });
        i += image[0].length;
        continue;
      }
      // malformed — consume one char as text and keep going.
      tokens.push({ kind: 'text', text: source[i] });
      i++;
      continue;
    }

    // 1b. Plain-text principle references: "arg11 P3", "arg11_P3". Must start
    // at a word boundary — if the previous char was alnum (e.g., "marginarg11")
    // we skip and treat as text. Case-insensitive.
    if ((source[i] === 'a' || source[i] === 'A') && isWordBoundaryStart(source, i)) {
      const rest = source.slice(i);
      const m = rest.match(PRINCIPLE_REF_RE);
      if (m) {
        const argNum = m[1];
        const pNum = m[2];
        tokens.push({
          kind: 'principle_ref',
          argomentoId: `arg${argNum}`,
          principleId: `arg${argNum}_P${pNum}`,
          raw: m[0],
        });
        i += m[0].length;
        continue;
      }
    }

    // 2. *** triple-star: emit italic_marker then bold_marker (closes innermost first)
    if (source[i] === '*' && source[i + 1] === '*' && source[i + 2] === '*') {
      tokens.push({ kind: 'italic_marker' });
      tokens.push({ kind: 'bold_marker' });
      i += 3;
      continue;
    }

    // 3. ** bold marker
    if (source[i] === '*' && source[i + 1] === '*') {
      tokens.push({ kind: 'bold_marker' });
      i += 2;
      continue;
    }

    // 4. * italic marker
    if (source[i] === '*') {
      tokens.push({ kind: 'italic_marker' });
      i++;
      continue;
    }

    // 5. ` code marker
    if (source[i] === '`') {
      tokens.push({ kind: 'code_marker' });
      i++;
      continue;
    }

    // plain character — merge with previous text token if possible
    const last = tokens[tokens.length - 1];
    if (last && last.kind === 'text') {
      last.text += source[i];
    } else {
      tokens.push({ kind: 'text', text: source[i] });
    }
    i++;
  }

  return tokens;
}

/**
 * Parse a flat token list into a ProseNode tree.
 * Returns the nodes produced and the final token index consumed.
 * `closingKind` is the marker kind that should close the current span (or null for top level).
 *
 * Strategy: greedy — consume tokens; when we see an opening marker, recurse.
 * If the matching closing marker is never found, we fall back and treat the
 * opening marker as raw text (unclosed marker → verbatim text, no crash).
 */
function parseTokens(
  tokens: Token[],
  start: number,
  closingKind: 'bold_marker' | 'italic_marker' | 'code_marker' | null
): { nodes: ProseNode[]; end: number } {
  const nodes: ProseNode[] = [];
  let i = start;

  while (i < tokens.length) {
    const tok = tokens[i];

    // Closing marker for current span?
    if (closingKind !== null && tok.kind === closingKind) {
      return { nodes, end: i + 1 }; // consume closing marker
    }

    if (tok.kind === 'cite') {
      nodes.push({ type: 'cite', id: tok.id });
      i++;
      continue;
    }

    if (tok.kind === 'principle_ref') {
      nodes.push({
        type: 'principleRef',
        argomentoId: tok.argomentoId,
        principleId: tok.principleId,
        raw: tok.raw,
      });
      i++;
      continue;
    }

    if (tok.kind === 'image') {
      nodes.push({
        type: 'image',
        imageKind: tok.imageKind,
        id: tok.id,
      });
      i++;
      continue;
    }

    if (tok.kind === 'text') {
      const last = nodes[nodes.length - 1];
      if (last && last.type === 'text') {
        (last as { type: 'text'; text: string }).text += tok.text;
      } else {
        nodes.push({ type: 'text', text: tok.text });
      }
      i++;
      continue;
    }

    // Opening markers — try to find matching close
    if (tok.kind === 'bold_marker') {
      // Look ahead: is there another bold_marker later?
      const hasClose = tokens.slice(i + 1).some((t) => t.kind === 'bold_marker');
      if (hasClose) {
        const inner = parseTokens(tokens, i + 1, 'bold_marker');
        nodes.push({ type: 'bold', children: inner.nodes });
        i = inner.end;
      } else {
        // unclosed — treat as text
        appendText(nodes, BOLD_MARKER);
        i++;
      }
      continue;
    }

    if (tok.kind === 'italic_marker') {
      const hasClose = tokens.slice(i + 1).some((t) => t.kind === 'italic_marker');
      if (hasClose) {
        const inner = parseTokens(tokens, i + 1, 'italic_marker');
        nodes.push({ type: 'italic', children: inner.nodes });
        i = inner.end;
      } else {
        appendText(nodes, ITALIC_MARKER);
        i++;
      }
      continue;
    }

    if (tok.kind === 'code_marker') {
      // code is special: collect raw text until next backtick, no nesting
      let raw = '';
      let j = i + 1;
      let closed = false;
      while (j < tokens.length) {
        if (tokens[j].kind === 'code_marker') {
          closed = true;
          j++;
          break;
        }
        raw += tokenToText(tokens[j]);
        j++;
      }
      if (closed) {
        nodes.push({ type: 'code', text: raw });
        i = j;
      } else {
        appendText(nodes, CODE_MARKER);
        i++;
      }
      continue;
    }

    // Should never reach here — safety fallback
    i++;
  }

  return { nodes, end: i };
}

function appendText(nodes: ProseNode[], text: string): void {
  const last = nodes[nodes.length - 1];
  if (last && last.type === 'text') {
    (last as { type: 'text'; text: string }).text += text;
  } else {
    nodes.push({ type: 'text', text });
  }
}

function tokenToText(tok: Token): string {
  switch (tok.kind) {
    case 'text': return tok.text;
    case 'bold_marker': return BOLD_MARKER;
    case 'italic_marker': return ITALIC_MARKER;
    case 'code_marker': return CODE_MARKER;
    case 'cite': return `[[cite:${tok.id}]]`;
    case 'principle_ref': return tok.raw;
    case 'image': return `[[image:${tok.imageKind}:${tok.id}]]`;
  }
}

function isWordBoundaryStart(source: string, i: number): boolean {
  if (i === 0) return true;
  const prev = source.charCodeAt(i - 1);
  // alpha: A-Z, a-z; digit 0-9; underscore — anything else is a boundary.
  const isAlnum =
    (prev >= 48 && prev <= 57) ||   // 0-9
    (prev >= 65 && prev <= 90) ||   // A-Z
    (prev >= 97 && prev <= 122) ||  // a-z
    prev === 95;                    // _
  return !isAlnum;
}

/**
 * Parse a single paragraph string into ProseNode[].
 * This is the INLINE entry point — bold/italic/code/cite/image/principleRef.
 * It does NOT split on \n\n or recognise block structures; ProfProse calls
 * parseBlocks() for that. Kept byte-for-byte stable so the ~9 existing
 * consumers + all existing inline tests continue to pass unchanged.
 */
export function parseProse(source: string): ProseNode[] {
  if (!source) return [];
  const tokens = tokenize(source);
  const { nodes } = parseTokens(tokens, 0, null);
  return nodes;
}

// ── BLOCK PARSER (FP3a) ─────────────────────────────────────────────────────
//
// parseBlocks() turns a full Prof answer into a sequence of BlockNode. It is
// resilient to INCOMPLETE structures mid-stream (ProfProse re-parses on every
// token append): a table whose separator row hasn't arrived yet, or a heading
// line still being typed, degrades to a paragraph until the structure is
// well-formed. Nothing here throws — malformed input always falls back to
// paragraph text, and we never surface raw pipes for a well-formed table.

const UNORDERED_ITEM_RE = /^[ \t]*[-*•]\s+(.*)$/;
const ORDERED_ITEM_RE = /^[ \t]*(\d{1,3})[.)]\s+(.*)$/;
const HEADING_RE = /^[ \t]*(#{2,3})\s+(.*\S)\s*$/;

/** A pipe-table row line: contains at least one `|`, after trimming. */
function isTableRowLine(line: string): boolean {
  return line.includes('|') && line.trim().length > 0;
}

/**
 * A table separator line: `|---|---|` / `| :--- | ---: |` etc. Each cell is
 * dashes with optional leading/trailing colons and surrounding spaces. Empty
 * cells (no dashes) make it NOT a separator, so a real data row of `| a | b |`
 * is never mistaken for one.
 */
function isTableSeparatorLine(line: string): boolean {
  const cells = splitTableCells(line);
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-{1,}:?$/.test(c.trim()));
}

/**
 * Split a pipe-table line into trimmed cell strings. Leading/trailing pipes are
 * optional (GitHub style). Escaped pipes (\|) inside a cell are preserved as a
 * literal '|'. Returns [] when there's no real cell content.
 */
function splitTableCells(line: string): string[] {
  let s = line.trim();
  // Drop one leading and one trailing unescaped pipe if present.
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);

  const cells: string[] = [];
  let current = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '\\' && s[i + 1] === '|') {
      current += '|';
      i++;
      continue;
    }
    if (ch === '|') {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

/** Pad/truncate a row's cells to exactly `width` so the grid stays rectangular. */
function normalizeRow(cells: string[], width: number): string[] {
  const out = cells.slice(0, width);
  while (out.length < width) out.push('');
  return out;
}

/** Inline-parse each cell string into ProseNode[]. */
function parseCells(cells: string[]): ProseNode[][] {
  return cells.map((c) => parseProse(c));
}

/**
 * Parse a run of consecutive non-blank lines (already split on \n) that begin
 * at `start` as a TABLE, IFF the run is well-formed:
 *   - line[0]              → header row (has a pipe)
 *   - line[1]              → separator row (dashes), cell count matches header
 *   - line[2..]            → body rows (each has a pipe)
 * Returns the table node + how many lines it consumed, or null if not a table.
 * A header-only or header+partial-separator run (mid-stream) returns null so
 * the caller degrades it to a paragraph until the separator fully arrives.
 */
function tryParseTable(lines: string[], start: number): { node: BlockNode; consumed: number } | null {
  const headerLine = lines[start];
  const sepLine = lines[start + 1];
  if (headerLine === undefined || sepLine === undefined) return null;
  if (!isTableRowLine(headerLine)) return null;
  if (!isTableSeparatorLine(sepLine)) return null;

  const headerCells = splitTableCells(headerLine);
  const width = headerCells.length;
  if (width === 0) return null;

  const bodyRows: string[][] = [];
  let i = start + 2;
  for (; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '' || !isTableRowLine(line)) break;
    bodyRows.push(normalizeRow(splitTableCells(line), width));
  }

  const node: BlockNode = {
    type: 'table',
    header: parseCells(normalizeRow(headerCells, width)),
    rows: bodyRows.map((r) => r.map((cell) => parseProse(cell))),
  };
  return { node, consumed: i - start };
}

/** Build a paragraph block from a run of lines, with single \n → break node. */
function paragraphFromLines(lines: string[]): BlockNode {
  const children: ProseNode[] = [];
  lines.forEach((line, idx) => {
    if (idx > 0) children.push({ type: 'break' });
    for (const node of parseProse(line)) children.push(node);
  });
  return { type: 'paragraph', children };
}

/**
 * Parse a full Prof answer into BlockNode[]. Blocks are separated by blank
 * lines (\n\n) just like the old <p> split, but within a block we now also
 * recognise headings, lists, and tables. A block with none of those structures
 * becomes a `paragraph` whose internal single newlines are hard breaks.
 *
 * Streaming safety: a half-arrived table (header present, separator missing)
 * renders as a paragraph; the moment the separator row exists it flips to a
 * table. Headings/lists are line-local so they're stable as they stream.
 */
export function parseBlocks(source: string): BlockNode[] {
  if (!source) return [];
  // Normalise CRLF so Windows-authored content and tests behave identically.
  const normalized = source.replace(/\r\n?/g, '\n');
  // Split into blank-line-separated chunks (one or more blank lines).
  const chunks = normalized.split(/\n[ \t]*\n+/);
  const blocks: BlockNode[] = [];

  for (const chunk of chunks) {
    if (chunk.trim() === '') continue;
    const lines = chunk.split('\n');
    parseChunkInto(lines, blocks);
  }

  return blocks;
}

/**
 * Parse the lines of a single blank-line-delimited chunk, pushing one or more
 * blocks. A chunk can contain a mix: e.g. a lead-in paragraph line directly
 * followed by list items, or a table. We walk the lines and group them.
 */
function parseChunkInto(lines: string[], out: BlockNode[]): void {
  let i = 0;
  // Buffer of plain lines that will flush as a single paragraph.
  let paraBuffer: string[] = [];

  const flushPara = () => {
    if (paraBuffer.length > 0) {
      out.push(paragraphFromLines(paraBuffer));
      paraBuffer = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    // 1. Heading (## / ###) — single line.
    const heading = line.match(HEADING_RE);
    if (heading) {
      flushPara();
      const level = (heading[1].length === 2 ? 2 : 3) as 2 | 3;
      out.push({ type: 'heading', level, children: parseProse(heading[2]) });
      i++;
      continue;
    }

    // 2. Table — needs header + separator on the next line.
    const table = tryParseTable(lines, i);
    if (table) {
      flushPara();
      out.push(table.node);
      i += table.consumed;
      continue;
    }

    // 3. Unordered list — collect consecutive `- ` / `* ` / `• ` items.
    if (UNORDERED_ITEM_RE.test(line)) {
      flushPara();
      const items: ProseNode[][] = [];
      while (i < lines.length) {
        const m = lines[i].match(UNORDERED_ITEM_RE);
        if (!m) break;
        items.push(parseProse(m[1]));
        i++;
      }
      out.push({ type: 'list', ordered: false, items });
      continue;
    }

    // 4. Ordered list — collect consecutive `1. ` / `2) ` items.
    if (ORDERED_ITEM_RE.test(line)) {
      flushPara();
      const items: ProseNode[][] = [];
      while (i < lines.length) {
        const m = lines[i].match(ORDERED_ITEM_RE);
        if (!m) break;
        items.push(parseProse(m[2]));
        i++;
      }
      out.push({ type: 'list', ordered: true, items });
      continue;
    }

    // 5. Plain line → buffer it into the current paragraph.
    paraBuffer.push(line);
    i++;
  }

  flushPara();
}

// Re-export for use by tests that want the raw CITE_RE
export { CITE_RE };
